// src/components/VoiceChatButton.tsx
// ============================================================================
// Voice Chat Mic Toggle Button
// ============================================================================

interface VoiceChatButtonProps {
  isVoiceEnabled: boolean;
  isMuted: boolean;
  isSpeaking: boolean;
  disabled?: boolean;
  language?: "en" | "ru";
  onToggle: () => void;
}

function MicIcon({ size = 20 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z" />
      <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
      <line x1="12" x2="12" y1="19" y2="22" />
    </svg>
  );
}

function MicOffIcon({ size = 20 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="2" x2="22" y1="2" y2="22" />
      <path d="M18.89 13.23A7.12 7.12 0 0 0 19 12v-2" />
      <path d="M5 10v2a7 7 0 0 0 12 5" />
      <path d="M15 9.34V5a3 3 0 0 0-5.68-1.33" />
      <path d="M9 9v3a3 3 0 0 0 5.12 2.12" />
      <line x1="12" x2="12" y1="19" y2="22" />
    </svg>
  );
}

export function VoiceChatButton({
  isVoiceEnabled,
  isMuted,
  isSpeaking,
  disabled = false,
  language = "en",
  onToggle,
}: VoiceChatButtonProps) {
  const getClassName = () => {
    const classes = ['voice-button', 'icon-button-circle'];

    if (!isVoiceEnabled) {
      // Not enabled yet - show as muted/off state
      classes.push('muted');
    } else if (isMuted) {
      classes.push('muted');
    } else if (isSpeaking) {
      classes.push('speaking');
    } else {
      classes.push('active');
    }

    return classes.join(' ');
  };

  const getAriaLabel = () => {
    if (!isVoiceEnabled) {
      return language === "ru" ? "Включить голосовой чат" : "Enable voice chat";
    }
    if (isMuted) {
      return language === "ru" ? "Включить микрофон" : "Unmute microphone";
    }
    return language === "ru" ? "Выключить микрофон" : "Mute microphone";
  };

  return (
    <button
      type="button"
      className={getClassName()}
      onClick={onToggle}
      disabled={disabled}
      aria-label={getAriaLabel()}
    >
      {!isVoiceEnabled || isMuted ? (
        <MicOffIcon size={20} />
      ) : (
        <MicIcon size={20} />
      )}
    </button>
  );
}
